/* eslint-disable no-loop-func */
/* eslint-disable no-param-reassign */
import storage from '@xto10x/storage/lib';
import cloneDeep from 'lodash.clonedeep';
import {
  clone,
  getDateObjectFromString,
  getValueFromQueryParam,
  hasProperty
} from '@xto10x/core/lib/app.utils';
import {
  convertUTCEpochToLocalTime,
  getSelectedRangeOKRsCopy
} from 'utils/app.utils';
import {
  StorageKey,
  UserRole,
  HeaderLeftTabs,
  ActivityTypeValue,
  ObjectiveTypes,
  QueryParam,
  ObjectiveCycleTypes,
  ObjectiveStatus
} from './constants';
import {
  objectiveOperationKeys,
  objectiveTypeMapping
} from '../containers/Settings/components/ModuleAccess/ObjectiveAccessManagement/utils';
import { format_date } from './moment.util';
import Theme from '../styles/theme';

// Dynamically import store to avoid circular dependency
const getStore = () => require('store').store;

// Helper functions for getFilterParametersAsStatement
const getLevelString = (filters, selectedTeam) => {
  if (
    selectedTeam?.tag_id === HeaderLeftTabs[2].tag_id &&
    (filters?.level || filters?.level === 0)
  ) {
    return `(upto L${filters.level})`;
  }
  return '';
};

const getFilterStatement = (key, filters, index) => {
  switch (key) {
    case 'objective_type':
    case 'objectives':
      // This is handled in the prefix, not here
      return null;

    case 'negotiation_states':
      if (filters.negotiation_states[0] === 'NEGOTIATION') {
        return 'Awaiting Acceptance';
      }
      return null;

    case 'objectives_not_updated_from_days':
      return `Not checked in ${filters[key]} days`;

    case 'progress_higher_than':
    case 'progress_lower_than': {
      // Handle progress filters together to avoid duplication
      if (filters.progress_higher_than && filters.progress_lower_than) {
        // Only process this once when we encounter the first progress filter
        if (
          Object.keys(filters).indexOf('progress_higher_than') < index ||
          Object.keys(filters).indexOf('progress_lower_than') < index
        ) {
          return null;
        }
        return `Progress ${filters.progress_higher_than} - ${filters.progress_lower_than}%`;
      }
      if (!filters.progress_higher_than) {
        return `Progress less than ${filters.progress_lower_than}%`;
      }
      return `Progress more than ${filters.progress_higher_than}%`;
    }

    case 'status':
      return ActivityTypeValue[filters.status[0]];

    case 'manager_approval_status':
      return 'Pending Manager Approval';

    case 'demographic_value':
      return filters.demographic_value;

    case 'level':
    case 'demographic_key':
      return null; // This is not needed as of now

    default:
      return 'Showing results';
  }
};

const buildFilterStatements = filters => {
  if (!filters) return 'Showing results';

  let finalStatement = '';
  const tempFilters = { ...filters };

  Object.keys(tempFilters).forEach((key, index) => {
    const statement = getFilterStatement(key, tempFilters, index);
    if (statement) {
      finalStatement += `${finalStatement.length > 0 ? ' : ' : ''}${statement}`;
    }
  });

  return finalStatement || 'Showing results';
};

const buildStatementPrefix = (filters, levelString, quickFilters) => {
  const selectedRangeOKRsCopy = getSelectedRangeOKRsCopy();
  const selectedObjTypes = filters?.objective_type?.split(',') || [];

  // Check if we should show "Company Aligned" prefix
  const isCompanyAligned =
    filters &&
    ((filters?.objectives?.length && filters.objectives[0] === 'COMPANY') ||
      filters.objective_type === ObjectiveTypes.company);

  if (isCompanyAligned) {
    return `# Company Aligned ${selectedRangeOKRsCopy} ${levelString} : `;
  }

  // Check if we should show "Objectives" prefix
  const shouldShowObjectivesPrefix =
    filters &&
    (filters.objectives ||
      selectedObjTypes.length === 0 ||
      (selectedObjTypes.includes(ObjectiveTypes.company) &&
        selectedObjTypes.includes(ObjectiveTypes.nonAligned)));

  if (shouldShowObjectivesPrefix) {
    return `# Objectives ${levelString} : `;
  }

  // Check if we should show "Independent Objectives" prefix
  const isNonAligned =
    selectedObjTypes[0] === ObjectiveTypes.nonAligned ||
    quickFilters?.objective_type === ObjectiveTypes.nonAligned;

  if (isNonAligned) {
    return '# Independent Objectives : ';
  }

  return '';
};

const objectivesUtils = {
  addKRAtPath: (objectivesList, newObj, rootObjectiveIndex, path) => {
    let index = parseInt(rootObjectiveIndex.replace('root_objective_', ''), 10);
    const objectiveCloneList = clone(objectivesList, []);
    let objective = objectiveCloneList[index]; // parent objective
    for (let i = 1; i < path.length; i += 1) {
      // going till the last
      index = objective.key_results.findIndex(
        item => item.objective_id === path[i]
      );
      if (index !== -1) objective = objective.key_results[index];
      else {
        // if dependency_objective
        return objectiveCloneList;
      }
    }

    if (objective.key_results == null) objective.key_results = [];

    objective.key_results = objective.key_results.filter(
      obj => obj.objective_id !== newObj.objective_id
    );

    index = objective.key_results.findIndex(
      item => item.objective_id === newObj.objective_id
    );
    if (!objective.alignment_of && index === -1) {
      objective.key_results.push(newObj);
    }
    objective.key_results_count += 1;
    if (!objective.sub_tree_accessible) objective.sub_tree_accessible = true;
    if (
      getDateObjectFromString(newObj.due_date).getTime() >
      getDateObjectFromString(objective.due_date).getTime()
    ) {
      objective.kr_due_date_exceeded = true;
    }

    return objectiveCloneList;
  },

  addKRAtPaths: (objectivesList, newObj, rootObjectiveIndexes) => {
    if (!rootObjectiveIndexes) return objectivesList;
    let objList = clone(objectivesList, []);
    Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex => {
      objList = objectivesUtils.addKRAtPath(
        objList,
        newObj,
        rootObjectiveIndex,
        rootObjectiveIndexes[rootObjectiveIndex]
      );
    });
    return objList;
  },

  updateObjectiveUsingPath: (
    objectivesList,
    objective,
    rootObjectiveIndex,
    path,
    updateKRs
  ) => {
    if (!path || !path.length) return;
    let index = parseInt(rootObjectiveIndex.replace('root_objective_', ''), 10);
    let currentObjective = objectivesList[index];
    if (path.length === 1) {
      let obj = {
        ...objective,
        alignment_of: currentObjective.alignment_of,
        isOpen: currentObjective.isOpen,
        dependency_objective: currentObjective.dependency_objective
      };
      if (!updateKRs || obj.alignment_of)
        obj = {
          ...obj,
          key_results: currentObjective.key_results,
          key_results_count: currentObjective.key_results_count,
          matching_objectives_in_subtree:
            currentObjective.matching_objectives_in_subtree
        };
      objectivesList.splice(index, 1, obj);
      return;
    }

    let parent = currentObjective;
    let isKr = false;

    for (let i = 1; i <= path.length - 1; i += 1) {
      parent = currentObjective;
      isKr =
        parent.key_results.filter(
          key_result => key_result.objective_id === path[i]
        ).length > 0;
      if (isKr) {
        index = parent.key_results.findIndex(
          item => item.objective_id === path[i]
        );
        currentObjective = currentObjective.key_results[index];
      }
    }
    if (isKr) {
      let obj = {
        ...objective,
        alignment_of: currentObjective.alignment_of,
        isOpen: currentObjective.isOpen,
        dependency_objective: currentObjective.dependency_objective,
        matching_objectives_in_subtree:
          currentObjective.matching_objectives_in_subtree
      };
      if (!updateKRs || obj.alignment_of)
        obj = {
          ...obj,
          key_results: currentObjective.key_results,
          key_results_count: currentObjective.key_results_count
        };
      if (obj.dependency_objective) {
        obj.dependent_objectives_count = 0;
      }
      parent.key_results.splice(index, 1, obj);
      parent.kr_due_date_exceeded = false;
      //  check if krs due date exceed parent's due date
      if (
        parent.key_results.findIndex(
          kr =>
            kr.due_date &&
            parent.due_date &&
            getDateObjectFromString(kr.due_date).getTime() >
              getDateObjectFromString(parent.due_date).getTime()
        ) > -1
      ) {
        parent.kr_due_date_exceeded = true; // add marker to parent's due date
      }
    }
  },

  updateObjectiveUsingPathToggleKr: (
    objectivesList,
    objective,
    rootObjectiveIndex,
    path
  ) => {
    if (!path || !path.length) return;
    let index = parseInt(rootObjectiveIndex.replace('root_objective_', ''), 10);
    let currentObjective = objectivesList[index];
    if (path.length === 1) {
      let obj = {
        ...objective,
        alignment_of: currentObjective.alignment_of,
        isOpen: objective.isOpen,
        dependency_objective: currentObjective.dependency_objective,
        matching_objectives_in_subtree:
          currentObjective.matching_objectives_in_subtree
      };
      if (obj.alignment_of)
        obj = {
          ...obj,
          key_results: currentObjective.key_results,
          key_results_count: currentObjective.key_results_count
        };
      objectivesList.splice(index, 1, obj);
      return;
    }

    let parent = currentObjective;
    let isKr = false;

    for (let i = 1; i <= path.length - 1; i += 1) {
      parent = currentObjective;
      isKr =
        parent.key_results.filter(
          key_result => key_result.objective_id === path[i]
        ).length > 0;
      if (isKr) {
        index = parent.key_results.findIndex(
          item => item.objective_id === path[i]
        );
        currentObjective = currentObjective.key_results[index];
      }
    }
    if (isKr) {
      let obj = {
        ...objective,
        alignment_of: currentObjective.alignment_of,
        isOpen: objective.isOpen,
        dependency_objective: currentObjective.dependency_objective,
        matching_objectives_in_subtree:
          currentObjective.matching_objectives_in_subtree
      };
      if (obj.alignment_of)
        obj = {
          ...obj,
          key_results: currentObjective.key_results,
          key_results_count: currentObjective.key_results_count
        };
      if (obj.dependency_objective) {
        obj.dependent_objectives_count = 0;
        obj.key_results = [];
        obj.key_results_count = 0;
        obj.isOpen = false;
      }
      parent.key_results.splice(index, 1, obj);
      parent.kr_due_date_exceeded = false;
      //  check if krs due date exceed parent's due date
      if (
        parent.visible &&
        parent.key_results.findIndex(
          kr =>
            kr.due_date &&
            parent.due_date &&
            getDateObjectFromString(kr.due_date).getTime() >
              getDateObjectFromString(parent.due_date).getTime()
        ) > -1
      ) {
        parent.kr_due_date_exceeded = true; // add marker to parent's due date
      }
    }
  },

  updateObjectiveUsingPaths: (
    objectivesList,
    objective,
    rootObjectiveIndexes,
    updateKRs
  ) => {
    if (!rootObjectiveIndexes) return objectivesList;
    Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex =>
      objectivesUtils.updateObjectiveUsingPath(
        objectivesList,
        objective,
        rootObjectiveIndex,
        rootObjectiveIndexes[rootObjectiveIndex],
        updateKRs
      )
    );
    return clone(objectivesList, []);
  },

  updateObjectiveUsingPathsToggleKr: (
    objectivesList,
    objective,
    rootObjectiveIndexes
  ) => {
    if (!rootObjectiveIndexes) return objectivesList;
    Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex =>
      objectivesUtils.updateObjectiveUsingPathToggleKr(
        objectivesList,
        objective,
        rootObjectiveIndex,
        rootObjectiveIndexes[rootObjectiveIndex]
      )
    );
    return clone(objectivesList, []);
  },

  getCurrentVersionOfObjective: (
    objectivesList,
    objective_id,
    rootObjectiveIndexes
  ) => {
    if (!rootObjectiveIndexes) return null;
    let currentVersionOfObjective = null;
    Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex => {
      const path = rootObjectiveIndexes[rootObjectiveIndex];

      if (!path || !path.length) return;
      const index = parseInt(
        rootObjectiveIndex.replace('root_objective_', ''),
        10
      );
      let currentObjective = objectivesList[index];
      if (currentObjective.objective_id === objective_id) {
        currentVersionOfObjective = currentObjective;
        return;
      }

      for (let i = 1; i < path.length; i += 1) {
        const parent = currentObjective;
        const key = parent.key_results.findIndex(
          item => item.objective_id === path[i]
        );

        if (key !== -1) {
          currentObjective = currentObjective.key_results[key];
          if (currentObjective.objective_id === objective_id) {
            currentVersionOfObjective = currentObjective;
            return;
          }
        } else {
          return;
        }
      }
    });
    return currentVersionOfObjective;
  },

  deleteObjectiveUsingPath: (
    objectivesList,
    objective,
    rootObjectiveIndex,
    path
  ) => {
    if (!path || !path.length) return;
    let index = parseInt(rootObjectiveIndex.replace('root_objective_', ''), 10);
    if (path.length === 1) {
      objectivesList.splice(index, 1);
      return;
    }
    let i;
    let obj = objectivesList[index];
    if (!obj) return;
    if (obj.key_results) {
      for (i = 1; i < path.length - 1; i += 1) {
        const ind = obj?.key_results.findIndex(
          item => item.objective_id === path[i]
        );
        if (ind === -1 || ind === undefined || ind === null) return;
        obj = obj?.key_results[ind];
      }
    }
    if (!obj) return;

    obj.kr_due_date_exceeded = false;
    //  check if krs due date exceed parent's due date
    if (
      obj.key_results?.findIndex(
        kr =>
          kr.due_date &&
          obj.due_date &&
          getDateObjectFromString(kr.due_date).getTime() >
            getDateObjectFromString(obj.due_date).getTime()
      ) > -1
    ) {
      obj.kr_due_date_exceeded = true; // add marker to parent's due date
    }

    if (
      obj.alignment_of &&
      obj.alignment_of.objective_id === objective.objective_id
    ) {
      objectivesList.splice(index, 1);
      return;
    }
    if (obj.key_results.length > 1) {
      index = obj.key_results.findIndex(
        item => item.objective_id === objective.objective_id
      );
      if (index !== -1) {
        obj.key_results.splice(index, 1);
        obj.key_results_count -= 1;
      }
      return;
    }
    obj.key_results = [];
    obj.key_results_count = 0;
    obj.kr_due_date_exceeded = false;
    obj.matching_objectives_in_subtree = 0;
  },

  deleteDependencyObjectivesUsingPath: (
    objectiveList,
    dependency_objective_id,
    linked_objective_id,
    rootObjectiveIndex,
    path
  ) => {
    if (!path || !path.length) return;
    const index = parseInt(
      rootObjectiveIndex.replace('root_objective_', ''),
      10
    );
    let currentObjective = objectiveList[index];
    // if root objective is the linked_objective
    if (currentObjective.objective_id === linked_objective_id) {
      currentObjective.key_results = currentObjective.key_results.filter(
        obj => obj.objective_id !== dependency_objective_id
      );
      currentObjective.key_results_count -= 1;
    }

    // if the linked objective is deep inside the pathMap tree
    for (let i = 1; i < path.length; i += 1) {
      const parent = currentObjective;
      const key = parent.key_results.findIndex(
        item => item.objective_id === path[i]
      );

      if (key !== -1) {
        currentObjective = currentObjective.key_results[key];
        if (currentObjective.objective_id === linked_objective_id) {
          currentObjective.key_results = currentObjective.key_results.filter(
            obj => obj.objective_id !== dependency_objective_id
          );
          currentObjective.key_results_count -= 1;
          return;
        }
      } else {
        return;
      }
    }
  },

  reduceDependentObjectivesCountForObjective: (
    objectiveListClone,
    rootObjectiveIndexes,
    dependency_objective_id
  ) => {
    if (rootObjectiveIndexes) {
      Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex => {
        const path = rootObjectiveIndexes[rootObjectiveIndex];
        if (!path || !path.length) return;
        const index = parseInt(
          rootObjectiveIndex.replace('root_objective_', ''),
          10
        );
        let currentObjective = objectiveListClone[index];
        // if root objective is the dependency_objective
        if (currentObjective.objective_id === dependency_objective_id) {
          currentObjective.dependent_objectives_count -= 1;
        }

        // if the dependency objective is deep inside the pathMap tree
        for (let i = 1; i < path.length; i += 1) {
          const parent = currentObjective;
          const key = parent.key_results.findIndex(
            item => item.objective_id === path[i]
          );

          if (key !== -1) {
            currentObjective = currentObjective.key_results[key];
            if (currentObjective.objective_id === dependency_objective_id) {
              currentObjective.dependent_objectives_count -= 1;
              return;
            }
          } else {
            return;
          }
        }
      });
    }
  },

  deleteDependencyObjectivesUsingPaths: (
    objectiveList,
    pathMap,
    linked_objective_ids,
    dependency_objective_id,
    isDependencyObjectiveDeleted = false
  ) => {
    const objectiveListClone = clone(objectiveList, []);
    linked_objective_ids.forEach(linked_objective_id => {
      const rootObjectiveIndexes = pathMap[linked_objective_id];
      if (!rootObjectiveIndexes) return null;
      Object.keys(rootObjectiveIndexes).forEach(rootObjectiveIndex => {
        objectivesUtils.deleteDependencyObjectivesUsingPath(
          objectiveListClone,
          dependency_objective_id,
          linked_objective_id,
          rootObjectiveIndex,
          rootObjectiveIndexes[rootObjectiveIndex]
        );
      });
    });

    // if the objective dependency is not deleted reduce the dependent_objectives_count
    if (!isDependencyObjectiveDeleted) {
      objectivesUtils.reduceDependentObjectivesCountForObjective(
        objectiveListClone,
        pathMap[dependency_objective_id],
        dependency_objective_id
      );
    }
    return objectiveListClone;
  },

  deleteObjectiveUsingPaths: (objectivesList, objective, paths) => {
    if (!paths) return objectivesList;
    const objectives = clone(objectivesList, []);
    Object.keys(paths).forEach(path =>
      objectivesUtils.deleteObjectiveUsingPath(
        objectives,
        objective,
        path,
        paths[path]
      )
    );
    return objectives;
  },

  updatePathMap: (objective, pathMap, basePath, rootObjectiveIndex) => {
    const { objective_id } = objective;
    const currentPath =
      basePath.length > 0 && basePath[basePath.length - 1] === objective_id
        ? basePath
        : basePath.concat(objective_id);

    let updatedPath = {
      ...pathMap[objective_id]
    };

    if (!objective.dependency_objective) {
      updatedPath = {
        ...pathMap[objective_id],
        [rootObjectiveIndex]: currentPath
      };
    }

    let updatedPathMap = { ...pathMap, [objective_id]: updatedPath };

    // for key_results
    if (objective.key_results?.length) {
      for (let i = 0; i < objective.key_results.length; i += 1) {
        updatedPathMap = objectivesUtils.updatePathMap(
          objective.key_results[i],
          updatedPathMap,
          currentPath,
          rootObjectiveIndex
        );
      }
    }

    return updatedPathMap;
  },

  createPathMap: dataList => {
    let updatedPathMap = {};
    for (let i = 0; i < dataList.length; i += 1) {
      updatedPathMap = objectivesUtils.updatePathMap(
        dataList[i],
        updatedPathMap,
        [],
        `root_objective_${i}`
      );
    }
    return updatedPathMap;
  },

  toggleObjectiveOpenStateAtPath: (objectiveList, obj, path) => {
    if (!path || !path.length) return objectiveList; // objective opened from notifications
    let index = parseInt(
      obj.rootObjectiveIndex.replace('root_objective_', ''),
      10
    );
    let objective = objectiveList[index];
    const isOpen = !obj.isOpen;
    const objectiveListClone = clone(objectiveList, []);
    if (path.length === 1) {
      objective.isOpen = isOpen;
      return objectiveListClone;
    }
    let currentObjective = objective;
    for (let i = 1; i <= path.length - 1; i += 1) {
      objective = currentObjective;
      index = currentObjective.key_results.findIndex(
        item => item.objective_id === path[i]
      );

      if (index !== -1) {
        currentObjective = currentObjective.key_results[index];
        if (currentObjective.objective_id === obj.objective_id) break;
      } else {
        // leave execution as it is a dependency objective
        break;
      }
    }
    if (index !== -1)
      objective.key_results.splice(index, 1, { ...obj, isOpen });
    return objectiveListClone;
  },

  getUpdatedAlignmentTree: (alignment, objective) => {
    let currentObjective = alignment.key_results[0];
    while (currentObjective.objective_id !== objective.objective_id) {
      currentObjective.alignment_of = objective;
      [currentObjective] = currentObjective.key_results;
    }
    currentObjective.key_results = objective.key_results;
    currentObjective.key_results_count = objective.key_results_count;
    currentObjective.isOpen = objective.isOpen;
    currentObjective.isAligned = true;
    return alignment;
  },

  replaceObjectiveAlignment: (dataList, oldObjective) => {
    const dataListClone = clone(dataList, []);
    const index = dataListClone.findIndex(
      obj =>
        obj.objective_id === oldObjective.objective_id &&
        obj.alignment_of &&
        obj.alignment_of.objective_id === oldObjective.alignment_of.objective_id
    );
    let objective = oldObjective;
    while (
      objective &&
      objective.objective_id !== oldObjective.alignment_of.objective_id
    ) {
      [objective] = objective.key_results;
    }
    const objectiveClone = clone(objective);
    objectiveClone.alignment_of = null;
    objectiveClone.isAligned = false;
    objectiveClone.root_objective = false;
    objectiveClone.parent_objective_id = null;
    objectiveClone.matching_objectives_in_subtree =
      oldObjective.alignment_of?.matching_objectives_in_subtree;
    dataListClone.splice(index, 1, objectiveClone);
    return dataListClone;
  },

  replaceObjectiveInList: (objectives, updatedObjective, index) => {
    const dataListClone = clone(objectives, []);
    dataListClone.splice(index, 1, updatedObjective);
    return dataListClone;
  },

  collapseObjectives: dataList =>
    dataList?.map(objective => {
      const parentObj = objective;
      if (parentObj.alignment_of) {
        let alignedObj = parentObj;
        while (
          alignedObj &&
          alignedObj.objective_id !== parentObj.alignment_of.objective_id
        ) {
          [alignedObj] = alignedObj.key_results;
        }
        if (alignedObj) {
          alignedObj.alignment_of = null;
          alignedObj.root_objective = false;
          alignedObj.parent_objective_id = null;
          alignedObj.isOpen = false;
          alignedObj.key_results = [];
          alignedObj.isAligned = false;
        }
        return alignedObj;
      }
      parentObj.isOpen = false;
      return parentObj;
    }),

  canPerformActions: (
    objective,
    user = storage.getDataFromLocal(StorageKey.USER_INFO),
    userRoles = getStore().getState()?.appReducer?.userRoles
  ) => {
    if (user == null || user.user_reference_id == null) return false;
    const { user_reference_id } = user;
    return (
      objective &&
      hasProperty(objective, 'assigner_id') &&
      hasProperty(objective, 'owner_detail') &&
      hasProperty(objective.owner_detail, 'user_reference_id') &&
      hasProperty(user, 'user_reference_id') &&
      (userRoles?.okr === UserRole.ADMIN ||
        user_reference_id === objective.assigner_id ||
        objective.parent_objective_owner === user_reference_id ||
        user_reference_id === objective.owner_detail.user_reference_id)
    );
  },

  shouldRestrictActions: (
    objective,
    user = storage.getDataFromLocal(StorageKey.USER_INFO),
    userRoles
  ) => !objectivesUtils.canPerformActions(objective, user, userRoles),

  isCurrentCycleLocked: cycle => {
    const cycleLockTime = cycle?.lock_at;
    if (
      cycleLockTime &&
      new Date() >= convertUTCEpochToLocalTime(cycleLockTime)
    )
      return true;
    return false;
  },

  getTeamSelectionForCreatedObjective: objective => {
    if (objective != null) {
      if (objective.isAnnualSpace) {
        return HeaderLeftTabs[2];
      }
      const objectiveOwner = objective && objective.owner_detail;
      const currentUser = storage.getDataFromLocal(StorageKey.USER_INFO);
      if (
        objectiveOwner &&
        objectiveOwner.user_reference_id === currentUser.user_reference_id
      ) {
        return HeaderLeftTabs[0];
      }
      if (
        objectiveOwner &&
        objectiveOwner.user_reference_id !== currentUser.user_reference_id
      ) {
        return HeaderLeftTabs[2];
      }
      return null;
    }
    return null;
  },

  getActiveObjectiveCycleIndex: (objectiveCycles, selectedObjCycleId) => {
    let activeObjectiveCycleIndex = -1;
    for (let i = 0; i <= objectiveCycles.length; i += 1) {
      // Find default activeObjectiveCycleIndex
      if (
        objectiveCycles[i] &&
        objectiveCycles[i].status === 'ACTIVE' &&
        activeObjectiveCycleIndex === -1
      ) {
        activeObjectiveCycleIndex = i;

        // If selectedObjCycleId is null or empty break loop
        if (selectedObjCycleId == null) {
          return i;
        }
      }

      // If selectedObjCycleId is not null, find it in objectiveCycles
      if (objectiveCycles[i] && selectedObjCycleId) {
        // If selectedObjCycleId matches objectiveCycles.id its a activeObjectiveCycleIndex
        if (selectedObjCycleId === objectiveCycles[i].id) {
          return i;
        }
      }
    }
    return activeObjectiveCycleIndex !== -1 ? activeObjectiveCycleIndex : 0;
  },

  getIfObjectiveIsContainedInACycle: (objective, objectiveCycles) => {
    const currentCycleForObjective = objectiveCycles
      .filter(cyc => cyc.cycle_type === ObjectiveCycleTypes.EXECUTION)
      .find(cyc => {
        const cycStartDate = new Date(cyc.start_date);
        const cycEndDate = new Date(cyc.end_date);

        const objStartDate = new Date(objective.start_date);
        const objEndDate = new Date(objective.due_date);

        if (
          cycStartDate.getTime() <= objStartDate.getTime() &&
          cycEndDate.getTime() >= objEndDate.getTime()
        ) {
          return cyc;
        }
        return null;
      });

    return currentCycleForObjective;
  },

  getIfCurrentObjectiveRangeIsSameAsSelectedCycleRange: (
    objective,
    objectiveCycles
  ) => {
    const isContainedCycleRangeSelected = objectivesUtils.isContainedCycleRange(
      objectiveCycles
    );
    const selectedObjectiveContainedCycle = objectivesUtils.getIfObjectiveIsContainedInACycle(
      objective,
      objectiveCycles
    );
    const matchingCustomCycle = objectivesUtils.getMatchingCycleFromCustomRange(
      objectiveCycles
    );
    if (
      isContainedCycleRangeSelected &&
      selectedObjectiveContainedCycle?.id === matchingCustomCycle?.id
    )
      return true;
    return false;
  },

  getNextCyclesForObjective: (objective, objectiveCycles) => {
    const currCycle = objectivesUtils.getIfObjectiveIsContainedInACycle(
      objective,
      objectiveCycles
    );

    if (!currCycle) return [];

    const validCycles = [];
    objectiveCycles
      .filter(cyc => cyc.cycle_type === ObjectiveCycleTypes.EXECUTION)
      .forEach(cyc => {
        const cycStartDate = new Date(cyc.start_date);
        const currEndDate = new Date(currCycle.end_date);

        if (cycStartDate.getTime() > currEndDate.getTime()) {
          validCycles.push({
            ...cyc,
            text: cyc.cycle_name,
            type: 'text'
          });
        }
      });

    return validCycles;
  },

  isContainedCycleRange: cycles => {
    const csd = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
    const ced = getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_END_DATE);
    const oc = getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE);

    const startDate = new Date(+csd);
    const endDate = new Date(+ced);

    const sDate = format_date(startDate, 'yyyy$-$Mm$-$dd');
    const eDate = format_date(endDate, 'yyyy$-$Mm$-$dd');

    for (let i = 0; i < cycles?.length; i++) {
      if (sDate === cycles[i].start_date && eDate === cycles[i].end_date) {
        return true;
      }
      if (
        oc === cycles[i].id &&
        cycles[i].cycle_type === ObjectiveCycleTypes.STRATEGIC
      ) {
        return true;
      }
    }

    return false;
  },

  getShouldLockObjective: (objective, objectiveCycles) =>
    !!objectivesUtils.getObjectiveExecutionCycle(objective, objectiveCycles)
      ?.lock_at && objective.company_objective_chain,

  getObjectiveExecutionCycle: (objective, objectiveCycles) => {
    if (!objective || !objectiveCycles?.length) return null;

    const objStartDate = new Date(objective.start_date);
    const objEndDate = new Date(objective.due_date);

    const cycle = objectiveCycles
      ?.filter(cyc => cyc.cycle_type === ObjectiveCycleTypes.EXECUTION)
      ?.find(cyc => {
        const cycleStartDate = new Date(cyc.start_date);
        const cycleEndDate = new Date(cyc.end_date);
        if (
          objStartDate.getTime() >= cycleStartDate.getTime() &&
          objEndDate?.getTime() <= cycleEndDate.getTime()
        ) {
          return cyc;
        }
        return null;
      });

    return cycle;
  },

  getMatchingCycleFromCustomRange: (
    cycles,
    objectiveCycle,
    customEndDate,
    customStartDate
  ) => {
    const csd =
      customStartDate ||
      getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_START_DATE);
    const ced =
      customEndDate ||
      getValueFromQueryParam(QueryParam.CUSTOM_DATE_RANGE_END_DATE);
    const oc =
      objectiveCycle || getValueFromQueryParam(QueryParam.OBJECTIVE_CYCLE);

    const startDate = new Date(+csd);
    const endDate = new Date(+ced);

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (ced && csd) {
      const matchingCycle = cycles
        .filter(cyc => cyc.cycle_type === ObjectiveCycleTypes.EXECUTION)
        .find(cyc => {
          const cycStartDate = new Date(cyc.start_date);
          const cycEndDate = new Date(cyc.end_date);

          cycStartDate.setHours(0, 0, 0, 0);
          cycEndDate.setHours(0, 0, 0, 0);

          if (
            cycStartDate.getTime() === startDate.getTime() &&
            cycEndDate.getTime() === endDate.getTime()
          )
            return cyc;
          return null;
        });
      return matchingCycle;
    }

    if (oc) {
      const matchingCycle = cycles
        .filter(cyc => cyc.cycle_type === ObjectiveCycleTypes.STRATEGIC)
        .find(cyc => cyc.id === oc);
      return matchingCycle;
    }
  },

  updateMatchingObjectiveFromListOfObjectives: (objectivesList, objective) => {
    if (objectivesList) {
      let clonedObjectivesList = cloneDeep(objectivesList);
      clonedObjectivesList = clonedObjectivesList.map(obj => {
        if (obj.objective_id === objective.objective_id) {
          const updated_objective = {
            ...objective,
            key_results_count: obj.key_results_count,
            key_results: obj.key_results
          };
          if (obj.isOpen) updated_objective.isOpen = true;
          return updated_objective;
        }
        if (obj?.key_results)
          obj.key_results = objectivesUtils.updateMatchingObjectiveFromListOfObjectives(
            obj.key_results,
            objective
          );
        return obj;
      });
      return clonedObjectivesList;
    }
    return objectivesList;
  },

  updateObjectiveDetailsRealtimeUpdate: (obj, updated_obj) => {
    if (obj && updated_obj) {
      updated_obj = {
        ...updated_obj,
        key_results_count: obj.key_results_count,
        key_results: obj.key_results
      };
      if (obj.isOpen) updated_obj.isOpen = true;
      return updated_obj;
    }
    return updated_obj;
  },

  isObjectivePresentInListOfObjectives: (
    objectivesList,
    objective,
    ignoreDependencyObjective = false,
    isPresent = false
  ) => {
    if (objectivesList) {
      const clonedObjectivesList = cloneDeep(objectivesList);
      isPresent = clonedObjectivesList.some(obj => {
        if (obj.visible && obj.objective_id === objective.objective_id) {
          if (ignoreDependencyObjective && obj.dependency_objective)
            return false;
          return true;
        }
        if (obj?.key_results && obj.key_results.length && obj.isOpen) {
          isPresent = objectivesUtils.isObjectivePresentInListOfObjectives(
            obj.key_results,
            objective,
            ignoreDependencyObjective
          );
        }
        return isPresent;
      });
      return isPresent;
    }
    return isPresent;
  },

  getFilterParametersAsStatement: (filters, selectedTeam, quickFilters) => {
    if (!filters) return 'Showing results';
    const levelString = getLevelString(filters, selectedTeam);
    const filterStatements = buildFilterStatements(filters);
    const prefix = buildStatementPrefix(filters, levelString, quickFilters);

    return `${prefix}${filterStatements}`;
  },

  reorderObjectives: (
    dataList,
    flatObjectives,
    objective_id,
    newIndex,
    oldIndex
  ) => {
    const dataListClone = cloneDeep(dataList);
    const sourceIndex = parseInt(
      flatObjectives[oldIndex].positionIndex.toString().split('.')[0],
      10
    );
    const destinationIndex = parseInt(
      flatObjectives[newIndex].positionIndex.toString().split('.')[0],
      10
    );

    const tempObj = dataListClone[sourceIndex];
    if (newIndex > oldIndex) {
      if (dataListClone[destinationIndex + 1]) {
        dataListClone.splice(destinationIndex + 1, 0, tempObj);
        dataListClone.splice(sourceIndex, 1);
      } else {
        dataListClone.push(tempObj);
        dataListClone.splice(sourceIndex, 1);
      }
    } else {
      dataListClone.splice(sourceIndex, 1);
      dataListClone.splice(destinationIndex, 0, tempObj);
    }
    return dataListClone;
  },

  getObjectiveFromPath: (dataList, objectivePath, objective_id) => {
    if (!objectivePath) return null;
    let obj = null;
    Object.keys(objectivePath).forEach(rootObjectiveIndex => {
      const path = objectivePath[rootObjectiveIndex];

      if (!path || !path.length) return;
      const index = parseInt(
        rootObjectiveIndex.replace('root_objective_', ''),
        10
      );
      let currentObjective = dataList[index];
      if (currentObjective.objective_id === objective_id) {
        obj = currentObjective;
        return;
      }

      for (let i = 1; i < path.length; i += 1) {
        const parent = currentObjective;
        const key = parent.key_results.findIndex(
          item => item.objective_id === path[i]
        );

        if (key !== -1) {
          currentObjective = currentObjective.key_results[key];
          if (currentObjective.objective_id === objective_id) {
            obj = currentObjective;
            return;
          }
        } else {
          return;
        }
      }
    });
    return obj;
  },

  isRootLevelReOrder: (dragResult, actualTree, flatTree) => {
    let destinationIndex = dragResult.destination.index;
    const sourceIndex = dragResult.source.index;
    if (
      destinationIndex > sourceIndex &&
      (flatTree[destinationIndex].depth !== 0 ||
        flatTree[destinationIndex].isOpen ||
        flatTree[destinationIndex].alignment_of)
    )
      destinationIndex += 1;
    const draggedObjectiveIndex = JSON.parse(dragResult.draggableId)
      .positionIndex;
    if (
      (typeof flatTree[destinationIndex]?.positionIndex === 'number' &&
        typeof draggedObjectiveIndex === 'number') ||
      !flatTree[destinationIndex]
    ) {
      return true;
    }
    return false;
  },

  recur: (x, depth, positionIndex, tempList, parent_objective_id) => {
    tempList.push({ ...x, positionIndex, parent_objective_id });
    if ((x.key_results.length && x.isOpen) || x.alignment_of) {
      return [
        ...x.key_results.map((y, ind) =>
          objectivesUtils.recur(
            y,
            depth + 1,
            positionIndex.concat(`.${ind}`),
            tempList,
            x.objective_id
          )
        )
      ];
    }
    return tempList;
  },

  flatDeep: list => {
    const listClone = cloneDeep(list);
    const tempList = [];
    if (listClone?.length)
      listClone.forEach((objTree, index) => {
        const depth = 0;
        tempList.push({ ...objTree, positionIndex: index });
        if (objTree.isOpen || objTree.alignment_of) {
          objTree.key_results.forEach((x, ind) =>
            objectivesUtils.recur(
              x,
              depth + 1,
              index.toString().concat(`.${ind}`),
              tempList,
              objTree.objective_id
            )
          );
        }
      });
    return tempList;
  },

  isUnderSameParentReorder: (dragResult, actualTree, flatTree) => {
    const sourceIndex = dragResult.source.index;
    let destinationIndex = dragResult.destination.index;
    if (
      destinationIndex > sourceIndex &&
      (flatTree[destinationIndex].isOpen ||
        flatTree[destinationIndex].alignment_of)
    )
      destinationIndex += 1;
    if (
      flatTree[sourceIndex]?.parent_objective_id ===
        flatTree[destinationIndex]?.parent_objective_id &&
      flatTree[sourceIndex]?.parent_objective_id
    )
      return true;
    return false;
  },

  reorderObjectivesUnderSameParent: (
    objectiveTree,
    flatObjectives,
    movedObjectiveId,
    parentObjectiveId,
    destinationIndex,
    sourceIndex,
    positionIndex
  ) => {
    const dataListClone = cloneDeep(objectiveTree);
    const position = positionIndex.length
      ? positionIndex.split('.')
      : positionIndex;
    let parentObjective = dataListClone[position[0]];
    for (let i = 1; i < position.length - 1; i++) {
      // going to the moved objective in the tree
      parentObjective = parentObjective.key_results[position[i]];
    }
    if (parentObjective.objective_id !== parentObjectiveId)
      return dataListClone; // ids don't match (this case should not happen)

    let i = 0;
    for (i = 0; i < parentObjective.key_results.length; i++) {
      // get index from where objective is moved
      if (parentObjective.key_results[i].objective_id === movedObjectiveId)
        break;
    }

    const movedObjectiveIndex = i;

    const replacedObjective = flatObjectives[destinationIndex];

    for (i = 0; i < parentObjective.key_results.length; i++) {
      // get destination objective
      if (
        parentObjective.key_results[i].objective_id ===
        replacedObjective.objective_id
      )
        break;
    }

    const destinationObjectiveIndex = i;

    if (sourceIndex > destinationIndex) {
      // objective moved upward
      const tempObj = cloneDeep(
        parentObjective.key_results[movedObjectiveIndex]
      );
      parentObjective.key_results.splice(movedObjectiveIndex, 1);
      parentObjective.key_results.splice(destinationObjectiveIndex, 0, tempObj);
      parentObjective.kr_order_version += 1;
    }

    if (sourceIndex < destinationIndex) {
      // objective moved downward
      const tempObj = cloneDeep(
        parentObjective.key_results[movedObjectiveIndex]
      );
      parentObjective.key_results.splice(
        destinationObjectiveIndex + 1,
        0,
        tempObj
      );
      parentObjective.key_results.splice(movedObjectiveIndex, 1);
      parentObjective.kr_order_version += 1;
    }
    return dataListClone;
  },

  reorderObjectivesUnderDiffParent: (
    objective,
    objectiveTree,
    flatObjectives,
    movedObjectiveId,
    parentObjectiveId,
    destinationIndex,
    sourceIndex,
    positionIndex
  ) => {
    const dataListClone = cloneDeep(objectiveTree);
    const position = positionIndex.length
      ? positionIndex.split('.')
      : [positionIndex];
    let parentObjective = dataListClone[position[0]];
    for (let i = 1; i < position.length - 1; i++) {
      // going to the moved objective in the tree
      parentObjective = parentObjective?.key_results[position[i]];
    }
    if (
      position.length > 1 &&
      parentObjective.objective_id !== parentObjectiveId
    )
      return dataListClone; // ids don't match (this case should not happen)

    let i = 0;
    for (i = 0; i < parentObjective?.key_results.length; i++) {
      if (parentObjective?.key_results[i].objective_id === movedObjectiveId)
        // find objective index in key results
        break;
    }
    let removeFromList = false;

    if (position.length > 1) {
      parentObjective?.key_results?.splice(i, 1); // objective removed from the original parent
      parentObjective.key_results_count -= 1;
    } else {
      removeFromList = true; // need to do it later
    }

    const replacedObjectivePositionIndex =
      flatObjectives[
        destinationIndex > sourceIndex ? destinationIndex + 1 : destinationIndex
      ]?.positionIndex || flatObjectives[destinationIndex]?.positionIndex;

    let objAddedAtTheEnd = false;
    if (!flatObjectives[destinationIndex + 1]) objAddedAtTheEnd = true;

    if (typeof replacedObjectivePositionIndex === 'number') {
      // objective is moved to the root level
      if (objAddedAtTheEnd) dataListClone.push(objective);
      else dataListClone.splice(replacedObjectivePositionIndex, 0, objective); // add objective at root level at position
    } else {
      // go to that position
      const newPos = replacedObjectivePositionIndex.split('.');
      let replacedObj = dataListClone[newPos[0]];
      for (i = 1; i < newPos.length - 1; i++) {
        // going to the moved objective in the tree
        replacedObj =
          replacedObj?.key_results && replacedObj.key_results[newPos[i]];
      }

      if (
        !objective.dependency_objective &&
        replacedObj.key_results?.find(
          obj =>
            obj.objective_id === objective.objective_id &&
            obj.dependency_objective
        )
      ) {
        replacedObj.key_results_count -= 1;
        replacedObj.key_results = replacedObj.key_results.filter(
          obj => obj.objective_id !== objective.objective_id
        );
      }
      replacedObj?.key_results?.splice(newPos[i], 0, objective);
      replacedObj.kr_order_version += 1;
      replacedObj.key_results_count += 1;
    }
    if (removeFromList) dataListClone.splice(position[0], 1); // remove obj from root level)
    return dataListClone;
  },

  shouldRestrictDrag: (objective, okrRole) => {
    const user = storage.getDataFromLocal(StorageKey.USER_INFO);
    if (!user) return true;
    if (okrRole === UserRole.ADMIN) return false;
    if (objective.parent_objective_owner === user.user_reference_id)
      return false;
    return true;
  },
  updateActivitiesRcnAdd: (
    { activity_details, check_in_details, comment_details },
    { association_id, emoji_id, userId }
  ) => {
    const updateActivity = activity => {
      if (
        activity.activity_id === association_id ||
        activity.comment_id === association_id
      ) {
        if (!activity?.reaction_attributes?.reaction_users_map) {
          activity.reaction_attributes = {
            reaction_users_map: {
              [emoji_id]: [userId]
            }
          };
          return activity;
        }

        if (!activity?.reaction_attributes?.reaction_users_map[emoji_id]) {
          activity.reaction_attributes = {
            ...activity.reaction_attributes,
            reaction_users_map: {
              ...activity.reaction_attributes.reaction_users_map,
              [emoji_id]: [userId]
            }
          };
          return activity;
        }

        if (activity?.reaction_attributes?.reaction_users_map[emoji_id]) {
          const reactionExists = activity.reaction_attributes.reaction_users_map[
            emoji_id
          ].includes(userId);
          const updatedUsersReactedList = [
            ...activity.reaction_attributes.reaction_users_map[emoji_id]
          ];
          if (!reactionExists) updatedUsersReactedList.push(userId);
          activity.reaction_attributes = {
            ...activity.reaction_attributes,
            reaction_users_map: {
              ...activity.reaction_attributes.reaction_users_map,
              [emoji_id]: updatedUsersReactedList
            }
          };
          return activity;
        }
      }
      return activity;
    };
    activity_details = (activity_details || []).map(activity => {
      activity = updateActivity(activity);
      return activity;
    });
    check_in_details = (check_in_details || []).map(activity => {
      activity = updateActivity(activity);
      return activity;
    });
    comment_details = (comment_details || []).map(activity => {
      activity = updateActivity(activity);
      return activity;
    });
    return {
      activity_details,
      check_in_details,
      comment_details
    };
  },

  updateActivitiesRcnRemove: (
    { activity_details, check_in_details, comment_details },
    { association_id, emoji_id, userId }
  ) => {
    const _updateActivity = activity => {
      if (
        activity.activity_id === association_id ||
        activity.comment_id === association_id
      ) {
        if (activity?.reaction_attributes?.reaction_users_map[emoji_id]) {
          const updatedReactionUsers = activity.reaction_attributes.reaction_users_map[
            emoji_id
          ].filter(id => id !== userId);
          activity.reaction_attributes = {
            ...activity.reaction_attributes,
            reaction_users_map: {
              ...activity.reaction_attributes.reaction_users_map,
              [emoji_id]: updatedReactionUsers
            }
          };
          if (!updatedReactionUsers.length)
            delete activity.reaction_attributes.reaction_users_map[emoji_id];
          return activity;
        }
      }
      return activity;
    };
    activity_details = (activity_details || []).map(activity => {
      activity = _updateActivity(activity);
      return activity;
    });
    check_in_details = (check_in_details || []).map(activity => {
      activity = _updateActivity(activity);
      return activity;
    });
    comment_details = (comment_details || []).map(activity => {
      activity = _updateActivity(activity);
      return activity;
    });
    return {
      activity_details,
      check_in_details,
      comment_details
    };
  },

  updateViewInContext: (
    objectiveTree,
    objective,
    objectiveContext,
    isLinkedObjective,
    objectiveLinkGettingViewedFor
  ) => {
    if (!objective) return objectiveTree;

    const listClone = cloneDeep(objectiveTree);
    if (objectiveContext) {
      // api call was made to get context
      const index = listClone.findIndex(
        obj => obj.objective_id === objectiveContext.objective_id
      );
      // check if the objective for which the context is present is already present in view
      // this was added primarily for the annual objective alignment view
      // primaryObjectiveIndex was not there before annual objective context view changes
      const primaryObjectiveIndex = listClone.findIndex(
        obj => obj.objective_id === objective.objective_id
      );
      if (index === -1 && primaryObjectiveIndex === -1) return listClone;
      let obj;
      if (index !== -1) {
        listClone[index] = objectiveContext;
        obj = listClone[index];
      } else {
        listClone[primaryObjectiveIndex] = objectiveContext;
        obj = listClone[primaryObjectiveIndex];
      }
      // TODO: Fix this
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (
          obj.objective_id === objective.objective_id &&
          !obj.dependency_objective
        ) {
          if (isLinkedObjective) {
            const linkedObj = obj.key_results.find(
              x => x.objective_id === objectiveLinkGettingViewedFor.objective_id
            );
            if (linkedObj) {
              obj.isOpen = true;
              linkedObj.isAligned = true;
            }
            break;
          } else {
            obj.isAligned = true;
            if (obj.key_results_count && obj.key_results.length)
              obj.isOpen = true;
            break;
          }
        }
        let krIndex = obj.key_results.findIndex(
          ob => ob.objective_id === objective.objective_id
        );
        if (krIndex > -1) {
          obj.isOpen = true;
          if (isLinkedObjective) {
            const linkedObj = obj.key_results[krIndex]?.key_results?.find(
              x => x.objective_id === objectiveLinkGettingViewedFor.objective_id
            );
            if (linkedObj) {
              obj.key_results[krIndex].isOpen = true;
              linkedObj.isAligned = true;
            }
            break;
          } else {
            obj.key_results[krIndex].isAligned = true;
            if (
              obj.key_results[krIndex]?.key_results_count &&
              obj.key_results[krIndex]?.key_results.length
            )
              obj.key_results[krIndex].isOpen = true;
            break;
          }
        }

        krIndex = obj.key_results.findIndex(
          ob => ob.key_results_count && ob.key_results.length
        );
        if (krIndex === -1) break;
        obj.isOpen = true;
        obj = obj.key_results[krIndex];
      }

      return listClone;
    }
    // objective is already present in view, just set isAligned
    const setAligned = obj => {
      if (
        obj.objective_id === objective.objective_id &&
        !obj.dependency_objective
      ) {
        if (isLinkedObjective) {
          const linkedObj = obj.key_results.find(
            x => x.objective_id === objectiveLinkGettingViewedFor.objective_id
          );
          if (linkedObj) {
            obj.isOpen = true;
            linkedObj.isAligned = true;
          }
          return;
        }
        obj.isAligned = true;
        if (obj.key_results_count && obj.key_results.length) obj.isOpen = true;
        return;
      }
      if (obj.key_results && obj.key_results_count)
        obj.key_results.forEach(setAligned);
    };

    for (let i = 0; i < listClone.length; i++) {
      const obj = listClone[i];
      if (
        obj.objective_id === objective.objective_id &&
        !obj.dependency_objective
      ) {
        if (isLinkedObjective) {
          const linkedObj = obj.key_results.find(
            x => x.objective_id === objectiveLinkGettingViewedFor.objective_id
          );
          if (linkedObj) {
            obj.isOpen = true;
            linkedObj.isAligned = true;
          }
          break;
        } else {
          obj.isAligned = true;
          if (obj.key_results_count && obj.key_results.length)
            obj.isOpen = true;
          break;
        }
      }

      if (obj.key_results && obj.key_results_count)
        obj.key_results.forEach(setAligned);
    }

    return listClone;
  },

  clearAlignedObjective: (objectiveTree, objective) => {
    if (!objective) return objectiveTree;
    const list = cloneDeep(objectiveTree);

    const clearAligned = obj => {
      if (obj.isAligned) obj.isAligned = false;
      if (obj.key_results && obj.key_results_count)
        obj.key_results.forEach(clearAligned);
    };

    for (let i = 0; i < list.length; i++) {
      const obj = list[i];
      if (obj.isAligned) obj.isAligned = false;
      if (obj.key_results && obj.key_results_count)
        obj.key_results.forEach(clearAligned);
    }

    return list;
  },

  getRootLevelUsers: objectivesList => {
    const users = [];
    if (!objectivesList || !objectivesList.length) return users;

    objectivesList.forEach(objective => {
      let owner;
      if (objective.alignment_of) owner = objective.alignment_of?.owner_detail;
      else owner = objective.owner_detail;
      // if user doesn't exist in users list, then add, otherwise leave
      if (
        owner &&
        users.findIndex(
          user => user.user_reference_id === owner.user_reference_id
        ) === -1
      )
        users.push(owner);
    });
    return users;
  },

  trimAiObjectiveSuggestion: suggestionList => {
    const validSuggestion = suggestionList.find(element =>
      element.includes('Objective')
    );
    let returnValue = validSuggestion?.split(':')[1].trim();
    if (!validSuggestion) [returnValue] = suggestionList;
    return returnValue;
  },

  trimAiKeyResults: suggestionList =>
    // const returnValue = suggestion.replace(/\n/g, '').split('KR');
    // returnValue.splice(0, 1);
    suggestionList,
  filterTasksFromComments: comments => {
    if (comments?.comments && comments.comments.length) {
      const taskMap = {};
      comments.comments.sort((a, b) => a.created_at - b.created_at);
      comments.comments.forEach(comment => {
        if (comment.task_model) {
          taskMap[comment.comment_id] = { ...comment, replies: [] };
        } else if (
          comment.parent_model &&
          taskMap[comment.parent_model.comment_id]
        ) {
          taskMap[comment.parent_model.comment_id].replies = [
            ...taskMap[comment.parent_model.comment_id].replies,
            comment
          ];
        }
      });
      // const tasks = comments.comments.filter(comment => !!comment.task_model);
      return Object.values(taskMap).sort((a, b) => b.created_at - a.created_at);
    }
    return null;
  }
};

export const currentUserRestrictions = (
  isAdmin,
  isUserManager,
  accessControlSettings
) => {
  const searchKey = isAdmin ? 'ADMIN' : isUserManager ? 'MANAGER' : 'USER';
  const settings = accessControlSettings?.find(
    item => item.persona === searchKey
  );
  return settings?.restricted_operations_map || {};
};

export const getIfUserHasRestrictedEditPermissions = (
  objective,
  userTypeRestrictions
) => {
  const objType = objective?.company_objective_chain
    ? objectiveTypeMapping.COMPANY.key
    : objectiveTypeMapping.NON_ALIGNED.key;

  const permissionType = objective?.root_objective
    ? objectiveOperationKeys.UPDATE_OBJECTIVE
    : objectiveOperationKeys.UPDATE_KR;

  if (userTypeRestrictions[objType]?.includes(permissionType)) {
    return true;
  }
  return false;
};

export const getIfUserHasRestrictedCreateObjectivePermission = (
  userTypeRestrictions,
  objType
) => {
  const permissionType = objectiveOperationKeys.CREATE_OBJECTIVE;

  if (userTypeRestrictions[objType]?.includes(permissionType)) {
    return true;
  }
  return false;
};

export const getIfUserHasRestrictedCreateKRPermission = (
  userTypeRestrictions,
  objType
) => {
  const permissionType = objectiveOperationKeys.CREATE_KR;

  if (userTypeRestrictions[objType]?.includes(permissionType)) {
    return true;
  }
  return false;
};

export const getIfUserHasRestrictedPermission = (
  objective,
  operation,
  userTypeRestrictions
) => {
  const objType = objective?.company_objective_chain
    ? objectiveTypeMapping.COMPANY.key
    : objectiveTypeMapping.NON_ALIGNED.key;

  if (userTypeRestrictions[objType]?.includes(operation)) {
    return true;
  }
  return false;
};

export const getAccessControlText = (objective, operation) => {
  const isUserManager = getStore().getState()?.appReducer?.isUserManager;
  const objectiveConfig = getStore().getState()?.appReducer?.objectiveConfig;
  const users = getStore().getState()?.inviteReducer.activeUsers;

  let adminName = '';

  if (users?.length) {
    adminName =
      users.find(user =>
        user.user_product_details_list?.find(
          u => u.product_reference_id === 'okr' && u.user_role === 'ADMIN'
        )
      )?.name || '';
  }

  if (adminName?.length) {
    // if its a company objective, don't show "your manager" in the tooltip
    if (objective?.company_objective_chain)
      return `This section is locked. Kindly contact ${adminName}(Admin) for making any changes.`;

    if (isUserManager)
      return `This section is locked. Kindly contact ${adminName}(Admin) for making any changes.`;

    // if user is not manager, check if manager has permissions for the same operation
    const managerRestrictions = currentUserRestrictions(
      false,
      true,
      objectiveConfig?.access_control
    );

    const isManagerRestricted = getIfUserHasRestrictedPermission(
      objective,
      operation,
      managerRestrictions
    );

    if (isManagerRestricted)
      return `This section is locked. Kindly contact ${adminName}(Admin) for making any changes.`;
    return `This section is locked. Kindly contact ${adminName}(Admin)/your manager for making any changes.`;
  }

  return 'This section is locked. Kindly contact your Admin for making any changes.';
};

export default objectivesUtils;

export const getNonOKRCycle = cycles => {
  if (cycles?.length) {
    return cycles.find(cyc => cyc.cycle_type === ObjectiveCycleTypes.NON_OKR);
  }
  return null;
};

export const addKRAtPaths = (objectivesList, obj, paths) =>
  objectivesUtils.addKRAtPaths(objectivesList, obj, paths);

export const updateObjectiveUsingPaths = (
  objectiveList,
  objective,
  paths,
  updateKRs
) =>
  objectivesUtils.updateObjectiveUsingPaths(
    objectiveList,
    objective,
    paths,
    updateKRs
  );

export const updateObjectiveUsingPathsToggleKr = (
  objectiveList,
  objective,
  paths
) =>
  objectivesUtils.updateObjectiveUsingPathsToggleKr(
    objectiveList,
    objective,
    paths
  );

export const deleteObjectiveUsingPaths = (objectiveList, objective, paths) =>
  objectivesUtils.deleteObjectiveUsingPaths(objectiveList, objective, paths);

export const deleteDependencyObjectivesUsingPaths = (
  objectiveList,
  pathMap,
  linked_objectives,
  dependency_objective_id
) =>
  objectivesUtils.deleteDependencyObjectivesUsingPaths(
    objectiveList,
    pathMap,
    linked_objectives,
    dependency_objective_id
  );

export const updatePathMap = (
  objective,
  pathMap,
  basePath,
  rootObjectiveIndex
) =>
  objectivesUtils.updatePathMap(
    objective,
    pathMap,
    basePath,
    rootObjectiveIndex
  );

export const createPathMap = dataList =>
  objectivesUtils.createPathMap(dataList);

export const toggleObjectiveOpenStateAtPath = (dataList, objective, pathMap) =>
  objectivesUtils.toggleObjectiveOpenStateAtPath(dataList, objective, pathMap);

export const replaceObjectiveInList = (dataList, data, objective) =>
  objectivesUtils.replaceObjectiveInList(dataList, data, objective);

export const collapseObjectives = dataList =>
  objectivesUtils.collapseObjectives(dataList);

export const getUpdatedAlignmentTree = (data, objective) =>
  objectivesUtils.getUpdatedAlignmentTree(data, objective);

export const replaceObjectiveAlignment = (data, oldObjective) =>
  objectivesUtils.replaceObjectiveAlignment(data, oldObjective);

export const getCurrentVersionOfObjective = (
  objectivesList,
  objective,
  rootObjectiveIndexes
) =>
  objectivesUtils.getCurrentVersionOfObjective(
    objectivesList,
    objective,
    rootObjectiveIndexes
  );

export const canPerformActions = (objective, user) =>
  objectivesUtils.canPerformActions(objective, user);

export const shouldRestrictActions = (objective, user) =>
  objectivesUtils.shouldRestrictActions(objective, user);

export const isCurrentCycleLocked = cycle =>
  objectivesUtils.isCurrentCycleLocked(cycle);

export const getTeamSelectionForCreatedObjective = objective =>
  objectivesUtils.getTeamSelectionForCreatedObjective(objective);

export const getActiveObjectiveCycleIndex = (
  objectiveCycles,
  selectedObjCycleId
) =>
  objectivesUtils.getActiveObjectiveCycleIndex(
    objectiveCycles,
    selectedObjCycleId
  );

export const getIfObjectiveIsContainedInACycle = (objective, objectiveCycles) =>
  objectivesUtils.getIfObjectiveIsContainedInACycle(objective, objectiveCycles);

export const getObjectiveExecutionCycle = (objective, objectiveCycles) =>
  objectivesUtils.getObjectiveExecutionCycle(objective, objectiveCycles);

export const getIfNextCycleExists = (objective, objectiveCycles) =>
  objectivesUtils.getIfNextCycleExists(objective, objectiveCycles);

export const getNextCyclesForObjective = (objective, objectiveCycles) =>
  objectivesUtils.getNextCyclesForObjective(objective, objectiveCycles);

export const isContainedCycleRange = cycles =>
  objectivesUtils.isContainedCycleRange(cycles);

export const getIfCurrentObjectiveRangeIsSameAsSelectedCycleRange = (
  objective,
  objectiveCycles
) =>
  objectivesUtils.getIfCurrentObjectiveRangeIsSameAsSelectedCycleRange(
    objective,
    objectiveCycles
  );

export const getShouldLockObjective = (objective, objectiveCycles) =>
  objectivesUtils.getShouldLockObjective(objective, objectiveCycles);

export const getMatchingCycleFromCustomRange = (
  cycles,
  objectiveCycle,
  customStartDate,
  customEndDate
) =>
  objectivesUtils.getMatchingCycleFromCustomRange(
    cycles,
    objectiveCycle,
    customStartDate,
    customEndDate
  );

export const updateMatchingObjectiveFromListOfObjectives = (
  objectivesList,
  objective
) =>
  objectivesUtils.updateMatchingObjectiveFromListOfObjectives(
    objectivesList,
    objective
  );

export const updateObjectiveDetailsRealtimeUpdate = (obj, updated_obj) =>
  objectivesUtils.updateObjectiveDetailsRealtimeUpdate(obj, updated_obj);

export const isObjectivePresentInListOfObjectives = (
  objectivesList,
  objective,
  ignoreDependencyObjective
) =>
  objectivesUtils.isObjectivePresentInListOfObjectives(
    objectivesList,
    objective,
    ignoreDependencyObjective
  );

export const getFilterParametersAsStatement = (
  filters,
  selectedTeam,
  quickFilters
) =>
  objectivesUtils.getFilterParametersAsStatement(
    filters,
    selectedTeam,
    quickFilters
  );

export const reorderObjectives = (
  dataList,
  flatObjectives,
  objective_id,
  newIndex,
  oldIndex
) =>
  objectivesUtils.reorderObjectives(
    dataList,
    flatObjectives,
    objective_id,
    newIndex,
    oldIndex
  );

export const getObjectiveFromPath = (dataList, objectivePath, objective_id) =>
  objectivesUtils.getObjectiveFromPath(dataList, objectivePath, objective_id);

export const flatDeep = (objectivesList, depth) =>
  objectivesUtils.flatDeep(objectivesList, depth);

export const isRootLevelReOrder = (dragResult, actualTree, flatTree) =>
  objectivesUtils.isRootLevelReOrder(dragResult, actualTree, flatTree);

export const isUnderSameParentReorder = (dragResult, actualTree, flatTree) =>
  objectivesUtils.isUnderSameParentReorder(dragResult, actualTree, flatTree);

export const reorderObjectivesUnderSameParent = (
  objectiveTree,
  flatObjectives,
  movedObjectiveId,
  parentObjectiveId,
  destinationIndex,
  sourceIndex,
  positionIndex
) =>
  objectivesUtils.reorderObjectivesUnderSameParent(
    objectiveTree,
    flatObjectives,
    movedObjectiveId,
    parentObjectiveId,
    destinationIndex,
    sourceIndex,
    positionIndex
  );

export const reorderObjectivesUnderDiffParent = (
  objective,
  objectiveTree,
  flatObjectives,
  movedObjectiveId,
  parentObjectiveId,
  destinationIndex,
  sourceIndex,
  positionIndex
) =>
  objectivesUtils.reorderObjectivesUnderDiffParent(
    objective,
    objectiveTree,
    flatObjectives,
    movedObjectiveId,
    parentObjectiveId,
    destinationIndex,
    sourceIndex,
    positionIndex
  );

export const shouldRestrictDrag = (objective, okrRole) =>
  objectivesUtils.shouldRestrictDrag(objective, okrRole);

export const updateActivitiesRcnAdd = (objectiveActivities, data) =>
  objectivesUtils.updateActivitiesRcnAdd(objectiveActivities, data);

export const updateActivitiesRcnRemove = (objectiveActivities, data) =>
  objectivesUtils.updateActivitiesRcnRemove(objectiveActivities, data);

export const updateViewInContext = (
  objectiveTree,
  objective,
  data,
  isLinkedObjective,
  objectiveLinkGettingViewedFor
) =>
  objectivesUtils.updateViewInContext(
    objectiveTree,
    objective,
    data,
    isLinkedObjective,
    objectiveLinkGettingViewedFor
  );

export const clearAlignedObjective = (objectiveTree, objective) =>
  objectivesUtils.clearAlignedObjective(objectiveTree, objective);

export const getRootLevelUsers = objectivesList =>
  objectivesUtils.getRootLevelUsers(objectivesList);

export const trimAiObjectiveSuggestion = suggestion =>
  objectivesUtils.trimAiObjectiveSuggestion(suggestion);

export const trimAiKeyResults = suggestion =>
  objectivesUtils.trimAiKeyResults(suggestion);

export const filterTasksFromComments = comments =>
  objectivesUtils.filterTasksFromComments(comments);

export const getColor = type => {
  if (type === ObjectiveStatus.DROPPED) return `${Theme.color.cool_grey}`;
  if (type === ObjectiveStatus.OFF_TRACK) return `${Theme.color.calypso_red}`;
  if (type === ObjectiveStatus.ON_TRACK) return `${Theme.color.muted_green}`;
  if (type === ObjectiveStatus.NOT_ACHIEVED)
    return `${Theme.color.periwinkle_lavender}`;
  if (type === ObjectiveStatus.NOT_STARTED)
    return `${Theme.color.sonic_silver}`;
  if (type === ObjectiveStatus.NEEDS_WORK)
    return `${Theme.color.sugar_honey_cashew}`;
  if (type === ObjectiveStatus.ACHIEVED) return `${Theme.color.air_force_blue}`;
  if (type === ObjectiveStatus.JUST_STARTING) return '#FFF5E5';
  if (type === ObjectiveStatus.MAKING_PROGRESS) return '#1DB510';
  if (type === ObjectiveStatus.GETTING_THERE) return '#182272';
  return `${Theme.color.base_100}`;
};

export const getBgColor = type => {
  if (type === ObjectiveStatus.DROPPED) return `${Theme.color.base_100}`;
  if (type === ObjectiveStatus.OFF_TRACK) return `${Theme.color.pale_red}`;
  if (type === ObjectiveStatus.ON_TRACK) return `${Theme.color.honeydew}`;
  if (type === ObjectiveStatus.NOT_ACHIEVED)
    return `${Theme.color.ghost_white}`;
  if (type === ObjectiveStatus.NOT_STARTED) return `${Theme.color.grey_95}`;
  if (type === ObjectiveStatus.NEEDS_WORK) return `${Theme.color.floral_white}`;
  if (type === ObjectiveStatus.ACHIEVED) return `${Theme.color.pattens_blue}`;
  if (type === ObjectiveStatus.JUST_STARTING) return '#FFF5E5';
  if (type === ObjectiveStatus.MAKING_PROGRESS) return '#ECF9EF';
  if (type === ObjectiveStatus.GETTING_THERE) return '#E5F1FF';
  return `${Theme.color.base_80}`;
};
